import { ARCHETYPE_LABEL, FAMILY_LABEL, type AgentDetail, type DetectionTier, type ScoreFactor, type Severity, type Signal, type SignalFamily } from "@ti/core";
import { CREDIBILITY_SESSIONS } from "@ti/core/risk";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Radar } from "lucide-react";
import { useMemo } from "react";
import { Legend } from "@/components/domain/charts";
import { SignalChip, TimeAgo } from "@/components/domain/misc";
import { Delta } from "@/components/domain/score";
import { TierBadge } from "@/components/domain/status";
import { QueryState } from "@/components/layout/page";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/misc";
import { MAGNITUDE_COLOR, SEVERITY_RANK } from "@/features/agents/bits";
import { ArsHistoryChart, PillarSmallMultiples } from "@/features/agents/score-charts";
import { FACTOR_KIND, factorText, fmtPoints, sortFactors } from "@/features/agents/score-drivers";
import { q } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/agents/$agentId/score")({
  component: ScoreTab,
});


const DETECTION_TIER_LABEL: Record<DetectionTier, string> = { D: "Deterministic", C: "Classifier", J: "LLM judge", S: "Statistical" };

function ScoreTab() {
  const { agentId } = Route.useParams();
  const agent = useQuery(q.agent(agentId));
  return (
    <QueryState
      query={agent}
      loading={
        <div className="flex flex-col gap-4">
          <Skeleton className="h-80" />
          <Skeleton className="h-48" />
        </div>
      }
    >
      {(a) => <ScoreBody agent={a} />}
    </QueryState>
  );
}

function ScoreBody({ agent: a }: { agent: AgentDetail }) {
  const h = a.scoreHistory;
  const first = h[0];
  const delta90 = first ? a.ars - first.ars : 0;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={`ARS history · ${h.length} days`}
          description="Dashed lines mark tier boundaries (T2 550 · T3 700 · T4 850)."
          actions={
            <span className="flex items-center gap-2">
              <span className="text-base font-semibold tabular">{a.ars}</span>
              <TierBadge tier={a.tier} />
              <Delta value={delta90} suffix={` ${h.length}d`} />
            </span>
          }
        />
        <CardBody className="pb-3">{h.length > 1 ? <ArsHistoryChart history={h} /> : <p className="py-12 text-center text-xs text-fg-3">Not enough history yet.</p>}</CardBody>
      </Card>

      <Card>
        <CardHeader title="Pillars over time" description="Shared scale across pillars, so slopes are directly comparable. Weight shown after each name." />
        <CardBody>{h.length > 1 ? <PillarSmallMultiples history={h} /> : <p className="py-8 text-center text-xs text-fg-3">Not enough history yet.</p>}</CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardHeader title="Score factors" description="Every score change stores its top contributing factors." />
          <CardBody className="p-0">
            <FactorsTable factors={a.topFactors} />
          </CardBody>
        </Card>
        <Card className="lg:col-span-5">
          <CardHeader title="Credibility" description="How much of the score comes from this agent's own sessions." />
          <CardBody>
            <CredibilityPanel agent={a} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Contributing signals"
          description="Signals detected on this agent, grouped by type. These feed the pillar penalties above."
          actions={
            <Link to="/signals" className="text-xs text-fg-3 hover:text-accent">
              Signal explorer
            </Link>
          }
        />
        <CardBody className="p-0">
          <SignalsTable agentId={a.id} />
        </CardBody>
      </Card>
    </div>
  );
}

const factorColumns: ColumnDef<ScoreFactor>[] = [
  {
    id: "factor",
    header: "Factor",
    meta: { className: "max-w-0 w-full" },
    cell: ({ row }) => {
      const f = row.original;
      return (
        <div className="flex min-w-0 items-baseline gap-2">
          {f.issueId ? (
            <Link to="/issues/$issueId" params={{ issueId: f.issueId }} className="shrink-0 font-mono text-[12px] text-fg-2 hover:text-accent hover:underline">
              {f.issueId}
            </Link>
          ) : null}
          <span className="truncate" title={factorText(f)}>
            {factorText(f)}
          </span>
        </div>
      );
    },
  },
  {
    id: "kind",
    header: "Effect",
    cell: ({ row }) => {
      const k = FACTOR_KIND[row.original.kind];
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-2 [&_svg]:size-3.5 [&_svg]:text-fg-3" title={k.help}>
          {k.icon}
          {k.label}
        </span>
      );
    },
  },
  {
    id: "impact",
    header: "Points",
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-semibold text-fg">{fmtPoints(row.original.impact)}</span>,
  },
];

function FactorsTable({ factors }: { factors: ScoreFactor[] }) {
  const data = useMemo(() => sortFactors(factors), [factors]);
  return <DataTable data={data} columns={factorColumns} dense empty={{ title: "No factors recorded yet" }} />;
}

function CredibilityPanel({ agent: a }: { agent: AgentDetail }) {
  const c = Math.max(0, Math.min(1, a.credibility));
  const observed = Math.round(c * CREDIBILITY_SESSIONS);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular">{fmtPct(c)}</span>
        <span className="text-[13px] text-fg-2">own-data</span>
      </div>
      <div>
        <div
          className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full"
          role="meter"
          aria-valuenow={Math.round(c * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Share of score from own data"
        >
          {c > 0 ? <div className="h-full rounded-full" style={{ width: `${c * 100}%`, background: MAGNITUDE_COLOR }} /> : null}
          {c < 1 ? <div className="h-full flex-1 rounded-full bg-surface-3" /> : null}
        </div>
        <div className="mt-2">
          <Legend
            items={[
              { label: `Own sessions ${fmtPct(c)}`, color: MAGNITUDE_COLOR },
              { label: `Segment prior ${fmtPct(1 - c)}`, color: "var(--surface-3)" },
            ]}
          />
        </div>
      </div>
      <pre className="overflow-x-auto rounded-md border bg-surface-2 px-3 py-2 font-mono text-[12px] leading-relaxed text-fg-2">
        {`ARS = c × ARS_own + (1 − c) × prior\nc   = min(1, sessions / ${fmtInt(CREDIBILITY_SESSIONS)})`}
      </pre>
      <p className="text-[13px] leading-relaxed text-fg-2">
        {c >= 1 ? (
          <>This agent has at least {fmtInt(CREDIBILITY_SESSIONS)} observed sessions, so its score is fully its own. The segment prior no longer contributes.</>
        ) : (
          <>
            About <span className="font-medium tabular text-fg">{fmtInt(observed)}</span> of {fmtInt(CREDIBILITY_SESSIONS)} sessions needed for full credibility. Until then the score is blended with the{" "}
            <span className="text-fg">
              {ARCHETYPE_LABEL[a.archetype]} · {a.autonomy}
            </span>{" "}
            segment prior. New agents start near the prior and earn their own score as data accumulates.
          </>
        )}
      </p>
    </div>
  );
}

interface SignalGroup {
  type: string;
  family: SignalFamily;
  severity: Severity;
  tier: DetectionTier;
  count: number;
  lastSeen: string;
  issueIds: string[];
}

function groupSignals(signals: Signal[]): SignalGroup[] {
  const map = new Map<string, SignalGroup>();
  for (const s of signals) {
    const g = map.get(s.type);
    if (!g) {
      map.set(s.type, { type: s.type, family: s.family, severity: s.severity, tier: s.tier, count: 1, lastSeen: s.ts, issueIds: s.issueId ? [s.issueId] : [] });
      continue;
    }
    g.count += 1;
    if (SEVERITY_RANK[s.severity] < SEVERITY_RANK[g.severity]) g.severity = s.severity;
    if (s.ts > g.lastSeen) g.lastSeen = s.ts;
    if (s.issueId && !g.issueIds.includes(s.issueId)) g.issueIds.push(s.issueId);
  }
  return [...map.values()].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.count - a.count);
}

const signalColumns: ColumnDef<SignalGroup>[] = [
  { id: "type", header: "Signal", accessorKey: "type", cell: ({ row }) => <SignalChip type={row.original.type} severity={row.original.severity} /> },
  { id: "severity", header: "Severity", accessorFn: (g) => SEVERITY_RANK[g.severity], cell: ({ row }) => <span className="text-xs capitalize text-fg-2">{row.original.severity}</span> },
  { id: "family", header: "Family", accessorFn: (g) => FAMILY_LABEL[g.family], cell: ({ getValue }) => <span className="text-fg-2">{getValue<string>()}</span> },
  {
    id: "tier",
    header: "Detection",
    accessorKey: "tier",
    cell: ({ row }) => (
      <span className="text-xs text-fg-2">
        <span className="font-mono text-fg-3">{row.original.tier}</span> · {DETECTION_TIER_LABEL[row.original.tier]}
      </span>
    ),
  },
  { id: "count", header: "Count", accessorKey: "count", meta: { align: "right" }, cell: ({ getValue }) => <span className="font-medium">{fmtInt(getValue<number>())}</span> },
  { id: "last", header: "Last seen", accessorKey: "lastSeen", cell: ({ row }) => <TimeAgo iso={row.original.lastSeen} className="text-xs" /> },
  {
    id: "issues",
    header: "Issue",
    accessorFn: (g) => g.issueIds[0] ?? "",
    cell: ({ row }) =>
      row.original.issueIds.length ? (
        <span className="flex flex-wrap gap-x-2">
          {row.original.issueIds.map((id) => (
            <Link key={id} to="/issues/$issueId" params={{ issueId: id }} className="font-mono text-[12px] text-fg-2 hover:text-accent hover:underline">
              {id}
            </Link>
          ))}
        </span>
      ) : (
        <span className="text-xs text-fg-3">—</span>
      ),
  },
];

function SignalsTable({ agentId }: { agentId: string }) {
  const signals = useQuery(q.signals({ agentId, limit: 500 }));
  const groups = useMemo(() => (signals.data ? groupSignals(signals.data) : undefined), [signals.data]);
  if (signals.error) {
    return (
      <div className="p-4">
        <QueryState query={signals}>{() => null}</QueryState>
      </div>
    );
  }
  return (
    <DataTable
      data={groups}
      columns={signalColumns}
      loading={signals.isPending}
      dense
      getRowId={(g) => g.type}
      empty={{ title: "No signals detected", description: "Clean so far: no detector has fired on this agent in the current window.", icon: <Radar /> }}
    />
  );
}
